import { skipToken } from "@reduxjs/toolkit/query";
import { COPILOT_ID } from "@repo/app-types/entities/copilot";
import { copilotProposalIdSchema } from "@repo/app-types/entities/copilotProposal";
import { Loader, LoaderStyle } from "@repo/ui/Loader/loader";
import { useEffect, useEffectEvent, useMemo, type FC } from "react";
import { Navigate, useParams, type To } from "react-router";
import {
  useGetPendingProposalQuery,
  useListThreadProposalsQuery,
} from "../api/entities/copilotProposal.js";
import { useMastraThreadQuery } from "../api/entities/mastraThread.js";
import { appPaths } from "../app/appPaths.js";
import {
  copilotThreadMetadataSchema,
  useCopilotProps,
} from "../features/copilot/copilotContext.js";
import { useNavigateKeepCopilotChat } from "../features/copilot/copilotPageContext.js";
import { buildCopilotProposalEditorLocation } from "../features/copilot/copilotProposalEditorLocation.js";

/**
 * The link an MCP proposal tool returns. Opens a pending proposal in its
 * editor, with the copilot thread it was saved under selected in the chat.
 * Anything else goes home: a malformed link, or a proposal that is resolved,
 * deleted, or one the teammate may not read.
 */
export const CopilotProposalRedirectPage: FC = () => {
  const { proposalId } = useParams();
  const parsedProposalId = copilotProposalIdSchema.safeParse(proposalId).data;

  // Only finds a pending proposal.
  const proposalQuery = useGetPendingProposalQuery(
    parsedProposalId ?? skipToken,
  );
  const threadId = proposalQuery.data?.thread_id;

  // The thread's summaries are what the editor locations are built from, and
  // what gives an automation proposal its playbook.
  const threadProposalsQuery = useListThreadProposalsQuery(
    threadId !== undefined ? { threadId } : skipToken,
  );
  const proposal = threadProposalsQuery.data?.find(
    (entry) => entry.id === parsedProposalId,
  );
  const editorLocation = useMemo(
    () =>
      proposal?.status === "pending"
        ? buildCopilotProposalEditorLocation(proposal)
        : undefined,
    [proposal],
  );

  // Companies without the copilot have no chat to select the thread in.
  const copilot = useCopilotProps();
  const threadQuery = useMastraThreadQuery(
    copilot && threadId !== undefined
      ? { agentId: COPILOT_ID, threadId }
      : skipToken,
  );
  const isThreadSettled =
    copilot === undefined || threadQuery.isSuccess || threadQuery.isError;

  const navigateKeepCopilotChat = useNavigateKeepCopilotChat();
  const openEditor = useEffectEvent((location: To) => {
    // A thread that fails to load or parse leaves the chat as it is.
    const thread = threadQuery.data;
    const metadata = copilotThreadMetadataSchema.safeParse(thread?.metadata);
    if (thread && metadata.success) {
      copilot?.onThreadSelect({
        id: thread.id,
        resourceId: thread.resourceId,
        title: thread.title,
        metadata: metadata.data,
      });
    }
    navigateKeepCopilotChat(location, { replace: true });
  });
  useEffect(() => {
    // Leaving the page before the thread settles would drop the selection.
    if (editorLocation === undefined || !isThreadSettled) {
      return;
    }
    openEditor(editorLocation);
  }, [editorLocation, isThreadSettled]);

  const isUnavailable =
    parsedProposalId === undefined ||
    proposalQuery.isError ||
    threadProposalsQuery.isError ||
    (threadProposalsQuery.data !== undefined && editorLocation === undefined);
  if (isUnavailable) {
    return <Navigate to={appPaths.home.build()} replace />;
  }

  return (
    <div className="flex min-h-full items-center justify-center">
      <Loader style={LoaderStyle.ZOOMIES} />
    </div>
  );
};
